// fallow-ignore-file complexity
// (An error sink is branchy by nature — message-variant matching, the
// ValidationError-vs-Error split, and the report funnel's degrade-don't-amplify
// guards. The branches ARE the point; fragmenting them hurts readability.)
//
// Front-end error sink. Writes uncaught errors to the `client_errors` table
// so admins can triage in-app without a phone call. Also forwards to Sentry
// when VITE_SENTRY_DSN is set.
//
// Usage:
//   reportError(err, { source: 'manual', context: { palletId: '...' } })
//
// The boundary + global handlers wired in main.tsx call this automatically.

import * as Sentry from '@sentry/react'
import { supabase } from '@/lib/supabase'
import { decodeJwtPayload } from '@/lib/offlineSession'
import { clearPersistedQueryCache } from '@/lib/queryPersist'
import { dbErrorCode } from '@/lib/messageFamily'
import { newUuid } from '@/lib/uuid'

type ErrorSource = 'boundary' | 'window' | 'unhandledrejection' | 'manual'

// ValidationError — thrown from mutationFns to signal a precondition the
// operator can fix in the UI (missing field, unsupported state, etc.).
// react-query catches it via onError and surfaces the message to the
// operator; the reporter pipeline below drops it so Sentry isn't spammed
// with "Capture a truck inspection photo first" entries that are working
// as designed.
export class ValidationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ValidationError'
  }
}

// True when a Supabase/PostgrestError is a UNIQUE-constraint violation
// (SQLSTATE 23505). An operator typing a name/code that already exists is an
// operator error, not an engineer bug — at a create/rename write site, do:
//   if (isUniqueViolation(e, 'chemicals_tenant_name_uniq'))
//     throw new ValidationError('A chemical with this name already exists …')
// so the message shows in the form AND reportError drops it (no Sentry page).
// The global mutation-cache subscriber (main.tsx) reports every raw mutation
// error, so an unmapped 23505 pages you for a typo. Pass `constraint` to narrow
// to one index when a table has several uniques. (Prod issue 89df4fbe.)
export function isUniqueViolation(err: unknown, constraint?: string): boolean {
  const e = err as { code?: unknown; message?: unknown } | null
  if (!e || (e.code as string | undefined) !== '23505') return false
  if (!constraint) return true
  const msg = typeof e.message === 'string' ? e.message : ''
  return msg.includes(constraint)
}

// Rethrow a failed write's error as a friendly ValidationError when it's a
// UNIQUE violation (optionally narrowed to `constraint`), otherwise rethrow it
// unchanged. Collapses the repeated create/rename guard
//   if (isUniqueViolation(e, c)) throw new ValidationError(msg); throw e
// into one call — keeps the mutationFn simple (fallow complexity) and DRY, and
// centralises the "operator typo, not an engineer page" decision. Returns
// `never` (it always throws), so call it as a statement:
//   if (error) throwUniqueOr(error, 'A mark with that code already exists.')
export function throwUniqueOr(error: unknown, message: string, constraint?: string): never {
  if (isUniqueViolation(error, constraint)) throw new ValidationError(message)
  throw error
}

// Errors we deliberately swallow. Mobile Safari kills IndexedDB connections
// when the tab is backgrounded; on resume, in-flight code touching IDB
// (Supabase Auth's session store, mostly) throws this. The connection
// reconnects on the next read, so the error is harmless but noisy in
// Sentry. Match the Safari + Chromium message variants.
const SWALLOWED_PATTERNS: RegExp[] = [
  /Connection to Indexed Database server lost/i,
  /The user denied permission to access the database/i,    // private mode + IDB
  /UnknownError.*indexeddb/i,
  // iOS Safari kills IDB transactions on backgrounding / memory pressure;
  // resumed code hits "no in-progress transaction". WebKit's message says
  // "database" not "indexedDB" so the regex above misses it. Sentry
  // 952d93efe70d415c (iOS 18.7 Mobile Safari, 2026-05-23). Same family
  // as the connection-lost filter above.
  /Attempt to get records from database without an in-progress transaction/i,
  // Mapbox-gl internal cleanup race: an async tile fetch resolves after
  // the map's been removed, and mapbox-gl tries to invoke its now-null
  // errorCb. Harmless — the user sees nothing because the map is gone.
  // Reported 2026-05-15 from iOS Mobile Safari on the dashboard route
  // (Sentry 39ccf17356ab448480879e3100d40f13). Known mapbox-gl-js issue.
  /errorCb is not a function/i,
  // Supabase auth token-lock race between concurrent tabs/refreshes.
  // supabase-js uses Web Locks to serialise token-refresh; when two
  // tabs (or the SupportViewBanner's refreshSession running alongside
  // a route load) both try to acquire it, the "loser" surfaces as an
  // unhandled rejection. The token still refreshes successfully — only
  // one request actually does the work. Cosmetic noise; ignore.
  // Reported 2026-05-18 from Chrome 147 (Sentry 52920a7b496447c4).
  /Lock ".*auth-token" was released because another request stole it/i,
  // Network-level fetch failure: the browser couldn't complete the request to
  // Supabase at all — TCP/DNS/TLS drop, offline, or the request aborted on
  // navigation / tab-backgrounding. Safari says "Load failed", Chromium says
  // "Failed to fetch". Transient + unactionable: react-query's retry +
  // refetchOnReconnect heal the UI on the next poll, and a genuine backend
  // outage shows up as many users at once + HTTP status codes, not these.
  // iOS Safari on flaky cellular dominates. Sentry 8f58db69 (iOS 18.7, the
  // Dashboard inventory-pulse 60s poll, 2026-06-02). The `(?: \(|$)` on the
  // second pattern excludes "Failed to fetch dynamically imported module"
  // (a stale-chunk load that lazyWithReload handles separately).
  /\bLoad failed\b/i,
  /Failed to fetch(?: \(|$)/i,
  // Cancelled requests. Three shapes seen live in client_errors over 8 days
  // (2026-07-28 telemetry pull): "AbortError: The user aborted a request."
  // (11 rows / 9 sessions), "AbortError: Request timed out after 30s (REST
  // timeout net)" (13 / 12), "AbortError: Fetch is aborted" (3 / 3) — Chromium,
  // our own REST timeout, and WebKit respectively. Every one is a request WE
  // cancelled: react-query aborts in-flight queries on unmount / key change,
  // and withTimeout's onTimeout hook now aborts too. A cancellation is not a
  // failure the user experienced, and counting it as friction actively
  // misleads — the timeout that caused it is already recorded as a
  // save_timeout with its own label.
  /\bAbortError\b/i,
  /The user aborted a request/i,
  /\bFetch is aborted\b/i,
  /The operation was aborted/i,
]

// DB-trigger operator validations — hard rules enforced server-side that the
// operator must FIX in the form (the mutation's onError shows the message to
// them). These are NOT engineering bugs, so they must not page Sentry. Distinct
// from the transient-noise list above: the UI usually soft-blocks these too, so
// the DB is the backstop — and hitting a backstop shouldn't wake an engineer.
// (Same intent as throwing a ValidationError, but the rule lives in a DB trigger
// so the error arrives as a Postgrest object, not a ValidationError instance.)
const DB_VALIDATION_PATTERNS: RegExp[] = [
  // mig 107 — pallet net weight per carton below the pack-type minimum.
  // e.g. "Pallet underweight for A15C Stargrow BLUE: 14.726 kg per carton, …"
  /Pallet underweight for /i,
  // migs 204/656 — transport rate-card gap: no transport_rates row covers the
  // mission's lookup date for (transporter, customer, truck type, capacity).
  // Operator fixes it in Finance → Transport or corrects the mission's truck
  // fields. Raised by apply_transport_rate_to_mission; the per-stop RPC's
  // skipped-report carries the same text. Sentry 8c75cb886c9a49eba074186dc538be9e.
  /no rate effective on .+ for this transporter \/ customer \/ truck/i,
  // save_pick_tallies piece-work backstops (migs 999a / 804 / 804) — all three
  // are operator-fixable conditions the capture screen already soft-guards, so
  // the DB RAISE is a backstop, not an engineer bug. They arrive as SQLSTATE
  // 42501 (insufficient_privilege), but so do the cross-tenant SECURITY
  // backstops in the same function ('… belongs to another tenant' / 'Pick not
  // found …') which we DELIBERATELY keep paging — so match on message, never on
  // the code. Sentry 53f23a9efa934cc1b7689045e9641672 (badge, 2026-07-24).
  //   • BADGE credit with no live badge behind it: a reissued/revoked card
  //     (old one still in the field) or a scan the 10-min-stale roster cache
  //     resolved just before it was revoked. UI: onBadgeScanned + friendlyTallyError.
  /Badge-identified piece-work requires a live badge belonging to that worker/i,
  // Shared-workers P5 (mig 20260730083028) — a HOST-side edit to a seconded
  // tally whose date sits in an APPROVED/PAID period at the EMPLOYING tenant.
  // Operator-fixable (the home office reopens the period, or the correction
  // waits for the next run). No SQLSTATE match — message only, as above.
  /Pay period already approved at the home business for that date/i,
  // Mig 20261024500000 — a pick whose drops sit in an APPROVED/PAID run
  // cannot be re-dated, re-blocked, re-moded or deleted (harvest_picks_pay_lock).
  // Operator-fixable (re-open the run, or leave the pick). Message only.
  /Cannot change this pick — piece work on it for/i,
  //   • FACE credit from a device that isn't an authorised FACE_KIOSK — a kiosk
  //     mis-provisioned or its token rotated. UI soft-gates on device_piecework_allowed.
  /Face-identified piece-work requires an authorized device/i,
  //   • Worker renumbered/removed between roster-load and save — the capture
  //     screen's inTenant() guard + onError roster-refresh already handle it;
  //     friendlyTallyError maps the message for the foreman.
  /Worker \S+ not found in current tenant/i,
  // mig 20260727100000 — un-allocating a cooler that a cooling stage still
  // points at (cooler_commodity_allocations_block_in_use). Dropping the row
  // would strand that stage on an unallocated room, so the DB refuses; the
  // message names the exact screen to clear it on first. CoolerAllocationSection
  // now soft-blocks the tick, so this is the backstop for a stale grid — the
  // operator fixes it in the form, which is not an engineer's problem.
  // Sentry af5d1ca7ffe749ee8906db901ca982ec (2026-07-28).
  /this cooler is still a stage room for lane /i,
  // mig 20260803113208 — BigHarvest ticket freeze: a supervisor writing into
  // a CLOSED pay week (typically a stale offline drain landing after the
  // Tuesday close). The office reopens the ticket or accepts the loss —
  // operator-fixable, never an engineering page.
  /ticket is closed — ask the office to reopen it/i,
  // mig 20261203500000 — a contract's contents are labour-managers-only. A
  // non-manager who opens a contract from the list reads this sentence on the
  // page (contractContents.ts); it is the rule working, not an engineer bug.
  // The payload RPCs (get_contract_payload) raise the longer "… or its
  // particulars." form, which this also matches.
  /Only a labour manager can open a contract/i,
]

export function isSwallowed(err: unknown): boolean {
  if (err instanceof ValidationError) return true
  const msg = extractMessage(err)
  return SWALLOWED_PATTERNS.some(re => re.test(msg))
      || DB_VALIDATION_PATTERNS.some(re => re.test(msg))
}

// The request never reached (or never returned from) the backend: offline,
// TCP/DNS/TLS drop, an abort, or a client-side timeout. Distinct from a
// server-delivered rejection — callers use this to say "check your
// connection" instead of blaming the resource (e.g. the buyer portal must
// not report a wifi blip as a revoked link).
const NETWORK_FAILURE_PATTERNS: RegExp[] = [
  /\bLoad failed\b/i,
  /Failed to fetch(?: \(|$)/i,
  /\bAbortError\b/i,
  /The user aborted a request/i,
  /\bFetch is aborted\b/i,
  /The operation was aborted/i,
  /took too long/i,          // withTimeout's client-side timeout wrapper
  /\bNetworkError\b/i,       // Firefox
  /timed out/i,
  // functions-js FunctionsFetchError — thrown exactly when fetch() itself
  // rejects, and its message never carries the underlying "Failed to fetch".
  /Failed to send a request to the Edge Function/i,
]

export function isNetworkFailure(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
  const msg = extractMessage(err)
  return NETWORK_FAILURE_PATTERNS.some(re => re.test(msg))
}

// React-Query passes the failed mutation/query error straight through, which
// for Supabase comes back as a PostgrestError-shaped plain object — `{ code,
// message, details, hint }` — not an Error instance. The previous reporter
// used `String(err)`, which yields the literal text "[object Object]" for
// those, hiding hundreds of real errors per week behind an opaque blob.
//
// extractMessage walks the common shapes (Error, PostgrestError, fetch
// Response-like objects, plain objects with a .message) and only falls
// through to JSON / String as a last resort. This unblocks debugging across
// every query/mutation cache subscriber error.
function extractMessage(err: unknown): string {
  if (err == null) return 'unknown error'
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  if (typeof err === 'object') {
    const o = err as Record<string, unknown>
    // PostgrestError + most Supabase responses
    if (typeof o.message === 'string' && o.message)             return o.message
    if (typeof o.error_description === 'string')                return o.error_description as string
    if (typeof o.error === 'string')                            return o.error as string
    if (typeof o.details === 'string' && o.details)             return o.details as string
    if (typeof o.hint    === 'string' && o.hint)                return o.hint    as string
    // fetch Response-shaped errors that some libs throw
    if (typeof o.statusText === 'string' && o.statusText)       return `HTTP ${o.status ?? '?'} — ${o.statusText}`
    try { return JSON.stringify(err).slice(0, 1000) } catch { /* fall through */ }
  }
  return String(err)
}

export interface ReportedContext {
  source?:  ErrorSource              // defaults to 'manual'
  context?: Record<string, unknown>  // arbitrary extra info
  // Sentry severity. Defaults to 'error'. Use 'info'/'warning' for expected,
  // handled events that should be queryable but must NOT page the team as errors
  // (e.g. the getSession self-heal breadcrumb — every legitimate self-heal would
  // otherwise raise an error-level alert). The client_errors row is written either way.
  level?:   'info' | 'warning' | 'error'
  // Bypass the isSwallowed early-return (M72). The DB_VALIDATION_PATTERNS / noise
  // swallows exist to stop a LIVE operator's routine backstop from paging the team.
  // But a permanently-stranded offline write (reportStrandedWrite, OP-5) can carry
  // the SAME raw DB text — e.g. "Worker F329 not found in current tenant" on a
  // renumbered-worker pick_tallies drop — and that is NOT operator noise: it is lost
  // pay that has already exhausted every retry and must stay centrally visible.
  // Set force:true ONLY on such already-terminal paths, never on a live mutation.
  force?:   boolean
}

// ---------------------------------------------------------------------------
// Per-tab session id — generated lazily on first use, persisted in
// sessionStorage so a single F5 keeps the same id but a new tab gets a new
// one. Lets us correlate multiple errors from the same user session.
// ---------------------------------------------------------------------------
const SESSION_KEY = 'kf49_session_id'
// Exported for uiTelemetry — telemetry rows share the per-tab session id so
// friction events and client_errors from the same session correlate.
export function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) {
      id = newUuid()
      sessionStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return 'no-storage'
  }
}

// ---------------------------------------------------------------------------
// Throttle — never send the same (source, message) more than once every 30s
// to avoid hammering Supabase if a render error fires repeatedly.
// ---------------------------------------------------------------------------
const recentlySent = new Map<string, number>()
const THROTTLE_MS  = 30_000
function shouldSend(key: string): boolean {
  const now  = Date.now()
  const last = recentlySent.get(key)
  if (last && now - last < THROTTLE_MS) return false
  recentlySent.set(key, now)
  // Keep the map bounded.
  if (recentlySent.size > 200) {
    const cutoff = now - THROTTLE_MS
    for (const [k, t] of recentlySent) if (t < cutoff) recentlySent.delete(k)
  }
  return true
}

// ---------------------------------------------------------------------------
// Stale-schema auto-recovery.
//
// A PWA client running a bundle cached from *before* a migration can query a
// column/table that's since been dropped — Postgres returns undefined_column
// (42703) / undefined_table (42P01), surfaced by PostgREST as
// "column X does not exist" / "relation X does not exist". This is the
// data-layer twin of a stale JS chunk (see lazyWithReload in App.tsx): the
// cure is identical — force the client onto fresh assets. We bounce through
// the ?reset-sw=1 escape hatch (unregisters the SW + clears the Cache API;
// it does NOT touch IndexedDB, so the offline queue survives), gated to ONCE
// per session so a genuine current-code schema bug can't loop. PROD-only so a
// developer mid-migration isn't yanked into a reload.
//
// Real incident (2026-06-01): mig 537 dropped growers.packhouse_tenant_id;
// a mobile-Safari PWA on a pre-retrofit bundle 400'd on the dashboard. Current
// code was clean — this is purely a cached-client problem, so the right fix is
// to refresh the client, not to touch the schema.
//
// Counter-incident (2026-08-21): the INVERTED drift — the deployed bundle was
// AHEAD of the schema (QC Checks tab selected qc_runs_summary.dispositioned_at
// with no view migration). A reset can never heal a schema that's behind, and
// the 60s re-arm this block used to have ejected operators to the home page on
// every visit to the tab until the migration landed. Hence the guard below is
// a hard once-per-session: the second 42703 after a reset attempt surfaces as
// a normal query error instead of another redirect.
// ---------------------------------------------------------------------------
function recoverFromStaleSchema(err: unknown): void {
  if (!import.meta.env.PROD) return
  if (typeof window === 'undefined') return
  const e    = err as { code?: unknown; message?: unknown } | null
  const code = typeof e?.code === 'string' ? e.code : ''
  const msg  = typeof e?.message === 'string' ? e.message : ''
  const isSchemaDrift =
    code === '42703' || code === '42P01' ||
    /\b(?:column|relation)\b[\s\S]*\bdoes not exist\b/i.test(msg)
  if (!isSchemaDrift) return
  try {
    const KEY = 'kf49_schema_reset_at'
    // Already attempted this session → the fresh bundle still hit it, so it's
    // a real bug, not staleness — stop, let the error surface. sessionStorage
    // survives the ?reset-sw=1 replace (same tab), so this genuinely holds
    // for the rest of the session; the old `< 60_000` window re-armed the
    // redirect forever on a bundle-ahead-of-schema bug (see header).
    if (sessionStorage.getItem(KEY)) return
    sessionStorage.setItem(KEY, String(Date.now()))
    // Wipe the durable React Query cache FIRST — ?reset-sw=1 clears the SW +
    // Cache API but leaves IndexedDB, so without this the persisted (old-shaped)
    // query cache would just restore on the next load and hit the same drift.
    void clearPersistedQueryCache().finally(() => {
      window.location.replace(window.location.origin + '/?reset-sw=1')
    })
  } catch { /* best-effort — recovery must never throw */ }
}

// ---------------------------------------------------------------------------
// Public API.
// ---------------------------------------------------------------------------
// When a pallet save is blocked underweight by the mig-107 DB trigger, fan a
// bell notification out to the tenant's configured recipients (mig 715,
// notify_underweight_attempt). Best-effort side-effect — never throws. Called
// from the global mutation-error subscriber so it catches EVERY pallet-save
// path (create, edit, repack) in one place. No-op until recipients are set.
export function fireUnderweightAlertIfMatch(err: unknown): void {
  const msg = extractMessage(err)
  if (!/Pallet underweight for /i.test(msg)) return
  // pallet_id from the /pallets/:id route when editing an existing pallet;
  // a create has no id yet, so label it generically.
  let palletId = '(new pallet)'
  try {
    const m = typeof location !== 'undefined' ? /\/pallets\/([^/?#]+)/.exec(location.pathname) : null
    if (m && m[1] && m[1] !== 'new') palletId = decodeURIComponent(m[1])
  } catch { /* ignore */ }
  try {
    void supabase
      .rpc('notify_underweight_attempt', { p_pallet_id: palletId, p_message: msg.slice(0, 1000) })
      .then(() => {}, () => {})   // swallow — alerting must never surface to the operator
  } catch { /* ignore */ }
}

export async function reportError(
  err: unknown,
  opts: ReportedContext = {},
): Promise<void> {
  // Drop known-noisy mobile-safari quirks + operator-fixable DB backstops before
  // they hit Sentry / DB — UNLESS the caller forced through (M72). A permanently-
  // stranded write (reportStrandedWrite) forces so a lost-pay signal that happens
  // to match an operator-noise pattern still surfaces; the live path never forces.
  if (!opts.force && isSwallowed(err)) return

  const source  = opts.source ?? 'manual'
  const message = extractMessage(err)
  const stack   = err instanceof Error ? err.stack ?? null : null
  const key     = `${source}::${message.slice(0, 200)}`

  if (!shouldSend(key)) return

  // Always log to console so dev / DevTools sees it too.
  // eslint-disable-next-line no-console
  console.error('[reportError]', source, err, opts.context)

  let userId:     string | null = null
  let userEmail:  string | null = null
  let tenantId:   string | null = null
  let tenantKind: string | null = null
  try {
    // getSession() not getUser() — getUser acquires the auth Web-Lock and
    // can race with token refreshes; this path runs *during* an error
    // handler, where we'd rather degrade than amplify the failure.
    const { data } = await supabase.auth.getSession()
    userId    = data.session?.user?.id    ?? null
    userEmail = data.session?.user?.email ?? null
    // Tenant context for triage (scale audit OP-4): decode it from the JWT
    // claims rather than an RPC — no extra round-trip on the error path, and
    // it works even when the network is the thing that failed.
    const token  = data.session?.access_token
    const claims = token ? decodeJwtPayload(token) : null
    const tid    = claims?.active_tenant_id
    const kind   = claims?.tenant_kind
    tenantId   = typeof tid  === 'string' && tid  ? tid  : null
    tenantKind = typeof kind === 'string' && kind ? kind : null
  } catch {
    // Auth failures shouldn't block error reporting.
  }

  // NB: tenant_id is intentionally NOT set here — client_errors.tenant_id has a
  // DEFAULT current_tenant_id() that already populates it (and satisfies the
  // RESTRICTIVE tenant_gate). Passing an explicit null when the JWT decode races
  // would OVERRIDE that default → fail the gate → silently drop the row.
  const row = {
    user_id:     userId,
    session_id:  sessionId(),
    source,
    message:     message.slice(0, 4000),
    stack:       stack?.slice(0, 8000) ?? null,
    url:         typeof location !== 'undefined' ? location.href : null,
    user_agent:  typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 500) : null,
    app_version: import.meta.env.VITE_APP_VERSION ?? null,
    context:     opts.context ?? null,
  }

  try {
    if (userId === null) {
      // No session (sign-in, /accept-invite, the portals, /render/*): the direct
      // INSERT has failed silently since mig 224 — tenant_id is stamped from the
      // claims and an anon session has none. report_client_error (mig
      // 20261023170003) is the anon-callable path: it stamps identity itself,
      // clamps every field and never raises, so the error path stays one hop.
      await supabase.rpc('report_client_error', {
        p_source:      source,
        p_message:     row.message,
        p_stack:       row.stack,
        p_url:         row.url,
        p_user_agent:  row.user_agent,
        p_app_version: row.app_version,
        p_context:     row.context,
        p_session_id:  row.session_id,
      })
    } else {
      await supabase.from('client_errors').insert(row)
    }
  } catch {
    // Last-resort log — if the DB itself is the source of the error we don't
    // want to recurse infinitely. Just drop it.
  }

  // Forward to Sentry too if it's been initialised (DSN set at build time).
  // Sentry.captureException is a no-op when init() wasn't called, so this is
  // safe even in environments without a DSN.
  try {
    const level = opts.level ?? 'error'
    // Tenant tags so an error spike is triageable by tenant/kind, not just user
    // (scale audit OP-4). Belt-and-braces with the global Sentry.setTag in
    // installGlobalErrorHandlers below — a per-event decode also catches the
    // window between a token refresh and the next auth-change callback.
    // db_code — the SQLSTATE / PostgREST code, when the thrown thing carried
    // one. Two jobs: an issue becomes searchable by code (`db_code:23505`), and
    // main.tsx's beforeSend can fingerprint a database error by code + message
    // family even on the captureMessage path below, where a PostgrestError is
    // a plain object and Sentry's `hint.originalException` is empty.
    const dbCode = dbErrorCode(err)
    const tags = {
      source,
      ...(tenantId   ? { tenant_id: tenantId }     : {}),
      ...(tenantKind ? { tenant_kind: tenantKind } : {}),
      ...(dbCode     ? { db_code: dbCode }         : {}),
    }
    // Attach the signed-in user so Sentry issues (and their email/Slack
    // notifications) show *who* hit the error, not just an opaque id. email
    // comes straight off the already-fetched session — no extra auth call.
    // POPIA note: this sends the operator's login email to Sentry; it's the
    // internal staff address, masked-text Replay is already on, and Sentry's
    // data-scrubbing applies. Drop `email` here if that ever needs revisiting.
    const sentryUser = userId || userEmail
      ? { id: userId ?? undefined, email: userEmail ?? undefined }
      : undefined
    if (err instanceof Error) {
      Sentry.captureException(err, {
        level,
        tags,
        extra:   opts.context,
        user:    sentryUser,
      })
    } else {
      Sentry.captureMessage(message, {
        level,
        tags,
        extra:  opts.context,
        user:   sentryUser,
      })
    }
  } catch {
    // Don't ever let Sentry itself crash the reporter.
  }

  // Last: if this was a stale-bundle-vs-migrated-schema mismatch, heal the
  // client (after the error's been logged above so we still see it once).
  recoverFromStaleSchema(err)
}

// ---------------------------------------------------------------------------
// install() — wire the global window handlers. Call once from main.tsx.
// ---------------------------------------------------------------------------
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return

  // Keep a GLOBAL Sentry tenant tag in sync with the session (scale audit OP-4).
  // Sentry's own globalHandlers integration auto-captures unhandled errors in
  // parallel with the window listeners below, and those events bypass
  // reportError's per-event tags — so tag the scope globally on every auth
  // change (fires with the initial session on subscribe, and on login/refresh/
  // switch/logout). Decode from the JWT; no RPC on this hot path.
  const claimTag = (v: unknown): string | undefined =>
    typeof v === 'string' && v && v !== 'undefined' && v !== 'null' ? v : undefined
  supabase.auth.onAuthStateChange((_e, session) => {
    const claims = session?.access_token ? decodeJwtPayload(session.access_token) : null
    Sentry.setTag('tenant_id',   claimTag(claims?.active_tenant_id))
    Sentry.setTag('tenant_kind', claimTag(claims?.tenant_kind))
  })

  window.addEventListener('error', (ev) => {
    void reportError(ev.error ?? ev.message, {
      source: 'window',
      context: { filename: ev.filename, lineno: ev.lineno, colno: ev.colno },
    })
  })

  window.addEventListener('unhandledrejection', (ev) => {
    void reportError(ev.reason, { source: 'unhandledrejection' })
  })
}
